/**
 * Server-sourced, human-readable messages for toasts (Unnati's pattern:
 * `notify("success", res.message)` / `e.response.data.message`).
 *
 * The backend (`utils/renderers.py` + `utils/exception_handler.py`) now sends:
 *  - on every successful POST/PUT/PATCH/DELETE: an `X-Message` header
 *    (percent-encoded) — "Customer created successfully" — and, where the body
 *    is a plain object without one, the same text as `message`;
 *  - on every failure: ONE sentence in `message` ("Last name is required.")
 *    plus the per-field map in `errors` (normalised by `normalizeError`).
 *
 * The axios response interceptor calls {@link captureServerMessage} for every
 * mutating response. Mutation `onSuccess` handlers then read it with
 * {@link serverMessage}, falling back to their own local text only when the
 * server sent none:
 *
 *   onSuccess: (res) => toast.success(serverMessage(res, 'Customer created'))
 *
 * Error handlers keep reading the normalised `ApiError.message`
 * (see {@link errorMessage}).
 */
import type { AxiosResponse } from 'axios'

export const MESSAGE_HEADER = 'x-message'

/** Non-enumerable key the interceptor stamps on response bodies. */
const MESSAGE_KEY = '__serverMessage'

/** How long a captured message stays claimable by an `onSuccess` handler. */
const FRESH_MS = 4000

const GENERIC = new Set([
  '',
  'success',
  'successful',
  'successfully',
  'ok',
  'done',
  'true',
  'created',
  'updated',
  'deleted',
  'fail',
  'failed',
  'error',
  'validation failed',
  'validation error',
  'invalid data',
  'invalid request',
  'bad request',
])

/** Whether *message* is a placeholder ("success", "Validation failed") that says nothing. */
export function isGenericMessage(message: unknown): boolean {
  if (typeof message !== 'string') return true
  return GENERIC.has(message.trim().replace(/[.!]+$/, '').toLowerCase())
}

function decodeHeader(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.trim()) return undefined
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

function readHeader(response: AxiosResponse): unknown {
  const headers = response.headers as unknown
  if (!headers) return undefined
  const getter = (headers as { get?: (name: string) => unknown }).get
  if (typeof getter === 'function') {
    try {
      const value = getter.call(headers, MESSAGE_HEADER)
      if (value != null) return value
    } catch {
      /* fall through to plain-object lookup */
    }
  }
  const record = headers as Record<string, unknown>
  return record[MESSAGE_HEADER] ?? record['X-Message']
}

/** The server's success sentence for *response*, if it sent one. */
export function extractServerMessage(response: AxiosResponse): string | undefined {
  const fromHeader = decodeHeader(readHeader(response))
  if (fromHeader && !isGenericMessage(fromHeader)) return fromHeader
  const body = response.data as Record<string, unknown> | null | undefined
  if (!body || typeof body !== 'object' || Array.isArray(body)) return undefined
  const message = body.message
  // A serialized object (has `id`, no envelope `status`) may have a model
  // FIELD named `message` — that's content, not a status sentence.
  const isObjectBody = 'id' in body && !('status' in body) && !('success' in body)
  if (typeof message === 'string' && !isGenericMessage(message) && !isObjectBody) {
    return message
  }
  return undefined
}

let lastMessage: { message?: string; at: number } | null = null

function stamp(target: unknown, message: string) {
  if (!target || typeof target !== 'object') return
  try {
    Object.defineProperty(target, MESSAGE_KEY, {
      value: message,
      enumerable: false,
      configurable: true,
      writable: true,
    })
  } catch {
    /* frozen / exotic object — the "last message" fallback still works */
  }
}

/**
 * Record the message of a mutating response (called by the axios
 * interceptor). The latest mutating response always wins — one WITHOUT a
 * message clears the previous one, so a stale message is never re-shown.
 */
export function captureServerMessage(response: AxiosResponse): void {
  const method = (response.config?.method || '').toLowerCase()
  if (!['post', 'put', 'patch', 'delete'].includes(method)) return
  const message = extractServerMessage(response)
  lastMessage = { message, at: Date.now() }
  if (message) {
    stamp(response, message)
    stamp(response.data, message)
  }
}

/** Copy a captured message onto a value an endpoint function derived from the response. */
export function withServerMessage<T>(value: T, source: unknown): T {
  const message = readStamp(source)
  if (message) stamp(value, message)
  return value
}

function readStamp(value: unknown): string | undefined {
  if (!value || typeof value !== 'object') return undefined
  const message = (value as Record<string, unknown>)[MESSAGE_KEY]
  return typeof message === 'string' && message ? message : undefined
}

/**
 * The server's message for the mutation that produced *result*, else
 * *fallback*. Reads the message stamped on the result object first, then the
 * most recent mutating response (claimed once, only while fresh).
 */
export function serverMessage(result: unknown, fallback: string): string {
  const stamped = readStamp(result)
  if (stamped) {
    lastMessage = null
    return stamped
  }
  if (lastMessage && Date.now() - lastMessage.at <= FRESH_MS) {
    const { message } = lastMessage
    lastMessage = null
    if (message) return message
  }
  return fallback
}

/** Reset captured state (tests). */
export function resetServerMessages(): void {
  lastMessage = null
}

/**
 * The human-readable text of a failed request: the normalised
 * `ApiError.message` (already one sentence from the server) — else *fallback*.
 * Never returns a raw `{field: [...]}` dump.
 */
export function errorMessage(err: unknown, fallback: string): string {
  const message = (err as { message?: unknown } | null | undefined)?.message
  if (typeof message === 'string' && message.trim() && !isGenericMessage(message)) {
    return message
  }
  const errors = (err as { errors?: Record<string, string[]> } | null | undefined)?.errors
  if (errors && typeof errors === 'object') {
    for (const [field, messages] of Object.entries(errors)) {
      const first = Array.isArray(messages) ? messages[0] : undefined
      if (typeof first === 'string' && first.trim()) return humanizeFieldError(field, first)
    }
  }
  return fallback
}

// ── Field-error humanisation (mirrors utils/error_messages.py) ────────────

const ACRONYMS: Record<string, string> = {
  ai: 'AI', api: 'API', csv: 'CSV', cta: 'CTA', gst: 'GST', gstin: 'GSTIN',
  html: 'HTML', id: 'ID', json: 'JSON', otp: 'OTP', pan: 'PAN', pdf: 'PDF',
  pin: 'PIN', qr: 'QR', sms: 'SMS', upi: 'UPI', url: 'URL', waba: 'WABA',
  whatsapp: 'WhatsApp',
}

const NON_FIELD = new Set(['non_field_errors', '__all__', 'detail', 'message', 'error', 'errors'])

/** `last_name` → `Last name`; `non_field_errors` → `''`. */
export function humanizeField(field: string): string {
  if (!field || NON_FIELD.has(field)) return ''
  const base = field.endsWith('_id') && field.length > 3 ? field.slice(0, -3) : field
  const words = base.replace(/[_-]+/g, ' ').trim().split(/\s+/)
  const out = words.map((word, i) => {
    const fixed = ACRONYMS[word.toLowerCase()]
    if (fixed) return fixed
    const lower = word.toLowerCase()
    return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower
  })
  return out.join(' ')
}

/** One `{field: "msg"}` entry → a sentence ("Last name is required."). */
export function humanizeFieldError(field: string, message: string): string {
  const text = String(message ?? '').trim()
  const label = humanizeField(field)
  if (!label) return text.charAt(0).toUpperCase() + text.slice(1)
  const lower = text.toLowerCase()
  if (
    lower === 'this field is required.' ||
    lower === 'this field may not be blank.' ||
    lower === 'this field may not be null.'
  ) {
    return `${label} is required.`
  }
  if (lower.includes('this field')) {
    return text.replace(/This field/g, label).replace(/this field/g, label.toLowerCase())
  }
  return text.charAt(0).toUpperCase() + text.slice(1)
}
